/**
 * Post-op auto-send — the shared MESSAGE: the default text/email, the variable chips, the draft validation, the
 * delay / quiet-hours helpers and the live preview. Pure.
 * Plan: docs/plans/2026-10-15-post-op-auto-send-plan.md §1 (message tab), §2 decisions 2/10, §5a.
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE, with NAMED exports only (no `export default`) — cypress/node-tests/post-op-editor.test.js
 *    executes this exact file, and teamio-admin/tests/post-op-defaults-twin.test.js loads it the same way.
 *    The reuse the plan asks for (src/utils/textInsert.js `spliceAtCursor`, src/utils/smsSegments.js `analyzeSms`)
 *    therefore happens in MessageTab.vue, not here.
 *
 * ⛔ POST_OP_DEFAULTS IS ONE CONSTANT IN THREE REPOS (plan decision 10): forms-service lib/postOp/defaults.js (the
 *    sender's fallback), teamio-admin lib/post-op-defaults.js (the onboarding seeder) and THIS file ("Reset to default
 *    message"). The twin test in teamio-admin fails — never skips — when any copy drifts. Change all three together.
 *
 * ⛔ THE TOKENS ARE THE REAL ONES every Teamio template already uses — {{site_name}}, {{site_officephone}},
 *    {{email_signature_block}} — NOT the design file's {{practice_name}} / {{office_phone}} / {{email_signature}}.
 *    The chip LABELS stay as designed.
 */

const DEFAULT_SMS_BODY =
  'Hi {{patient_first_name}}, thanks for visiting {{site_name}} today. Your after-care instructions:\n{{post_op_links}}\nQuestions? Call {{site_officephone}}';

const DEFAULT_EMAIL_SUBJECT = 'Your after-care instructions from {{site_name}}';

const DEFAULT_EMAIL_BODY =
  'Hi {{patient_first_name}},\n\nThank you for visiting us today. Your after-care instructions are below. Please read them when you get home.\n\n{{post_op_links}}\n\nIf anything doesn\'t feel right, or you have questions, call us at {{site_officephone}}.\n\n{{email_signature_block}}';

export const POST_OP_DEFAULTS = Object.freeze({
  enabled: 'FALSE',
  liveSince: null,
  delayMinutes: 60,
  quietStart: '08:00',
  quietEnd: '20:00',
  sms: Object.freeze({ body: DEFAULT_SMS_BODY }),
  email: Object.freeze({ subject: DEFAULT_EMAIL_SUBJECT, body: DEFAULT_EMAIL_BODY }),
});

// ── Variable chips ───────────────────────────────────────────────────────────────────────────────────────────────

export const POST_OP_LINKS_TOKEN = '{{post_op_links}}';
export const SIGNATURE_TOKEN = '{{email_signature_block}}';

/** The chips under the message box. `required` chips are flagged until the token is in the text. */
export const SMS_CHIPS = Object.freeze([
  Object.freeze({ label: 'First name', token: '{{patient_first_name}}' }),
  Object.freeze({ label: 'Last name', token: '{{patient_last_name}}' }),
  Object.freeze({ label: 'Practice name', token: '{{site_name}}' }),
  Object.freeze({ label: 'Office phone', token: '{{site_officephone}}' }),
  Object.freeze({ label: 'Post-op links', token: POST_OP_LINKS_TOKEN, required: true }),
]);

/** The email gets the same chips plus the office's signature. */
export const EMAIL_CHIPS = Object.freeze(SMS_CHIPS.concat([
  Object.freeze({ label: 'Email signature', token: SIGNATURE_TOKEN }),
]));

/** "{{post_op_links}}" with optional inner whitespace — the one required variable (the server's POST_OP_LINKS_TOKEN_RE). */
export function hasPostOpLinks(text) {
  return /\{\{\s*post_op_links\s*\}\}/.test(String(text === null || text === undefined ? '' : text));
}

/** Is this token (e.g. '{{site_name}}') anywhere in the text? Tolerant of inner whitespace. */
export function hasToken(text, token) {
  const name = String(token).replace(/^\{\{\s*/, '').replace(/\s*\}\}$/, '');
  return new RegExp(`\\{\\{\\s*${name}\\s*\\}\\}`).test(String(text === null || text === undefined ? '' : text));
}

// ── Validation (mirrors forms-service lib/postOp/settings.js validateSettings — the SERVER is the gate) ─────────

export const MAX_DELAY_MINUTES = 1440;
export const MAX_SMS_BODY_CHARS = 1600;
export const MAX_EMAIL_SUBJECT_CHARS = 200;
export const MAX_EMAIL_BODY_CHARS = 20000;
export const MISSING_LINKS_MESSAGE = 'Add the Post-op links variable so patients get their instructions.';

const HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

function nonBlank(v) {
  return typeof v === 'string' && v.trim() !== '';
}

/**
 * The editable part of POST_OP_SETTINGS → the problems to show. `draft` = { delayMinutes, quietStart, quietEnd,
 * sms:{body}, email:{subject, body} }. Returns { errors:{ 'sms.body'?:msg, … }, count } — one message per field, so
 * "Fix N issues on Post-op message to save" counts fields.
 */
export function validateMessageDraft(draft) {
  const d = draft || {};
  const errors = {};

  const delay = d.delayMinutes;
  if (!Number.isInteger(delay) || delay < 0 || delay > MAX_DELAY_MINUTES) {
    errors.delayMinutes = `Enter a whole number of minutes from 0 to ${MAX_DELAY_MINUTES} (24 hours).`;
  }

  if (!HHMM_RE.test(String(d.quietStart)) || !HHMM_RE.test(String(d.quietEnd))) {
    errors.window = 'Pick a start and an end time.';
  } else if (!(d.quietStart < d.quietEnd)) {
    errors.window = 'The sending window must end after it starts.';
  }

  const sms = d.sms && typeof d.sms === 'object' ? d.sms : {};
  if (!nonBlank(sms.body)) errors['sms.body'] = 'The text message cannot be blank.';
  else if (sms.body.length > MAX_SMS_BODY_CHARS) errors['sms.body'] = `The text message is too long (max ${MAX_SMS_BODY_CHARS} characters).`;
  else if (!hasPostOpLinks(sms.body)) errors['sms.body'] = MISSING_LINKS_MESSAGE;

  const email = d.email && typeof d.email === 'object' ? d.email : {};
  if (!nonBlank(email.subject)) errors['email.subject'] = 'The email subject cannot be blank.';
  else if (email.subject.length > MAX_EMAIL_SUBJECT_CHARS) errors['email.subject'] = `The email subject is too long (max ${MAX_EMAIL_SUBJECT_CHARS} characters).`;
  else if (/[\r\n]/.test(email.subject)) errors['email.subject'] = 'The email subject must be a single line.';

  if (!nonBlank(email.body)) errors['email.body'] = 'The email message cannot be blank.';
  else if (email.body.length > MAX_EMAIL_BODY_CHARS) errors['email.body'] = `The email message is too long (max ${MAX_EMAIL_BODY_CHARS} characters).`;
  else if (!hasPostOpLinks(email.body)) errors['email.body'] = MISSING_LINKS_MESSAGE;

  return { errors, count: Object.keys(errors).length };
}

/** The part of the server's settings the Message tab edits (never `enabled` / `liveSince` — the switch and the server own those). */
export function draftFromSettings(settings) {
  const s = settings && typeof settings === 'object' ? settings : {};
  const sms = s.sms && typeof s.sms === 'object' ? s.sms : {};
  const email = s.email && typeof s.email === 'object' ? s.email : {};
  return {
    delayMinutes: Number.isInteger(s.delayMinutes) ? s.delayMinutes : POST_OP_DEFAULTS.delayMinutes,
    quietStart: nonBlank(s.quietStart) ? s.quietStart : POST_OP_DEFAULTS.quietStart,
    quietEnd: nonBlank(s.quietEnd) ? s.quietEnd : POST_OP_DEFAULTS.quietEnd,
    sms: { body: typeof sms.body === 'string' ? sms.body : POST_OP_DEFAULTS.sms.body },
    email: {
      subject: typeof email.subject === 'string' ? email.subject : POST_OP_DEFAULTS.email.subject,
      body: typeof email.body === 'string' ? email.body : POST_OP_DEFAULTS.email.body,
    },
  };
}

// ── "Send [60] [minutes|hours] after…" / "Only between [8:00 AM] and [8:00 PM]" ──────────────────────────────

export const DELAY_UNITS = Object.freeze(['minutes', 'hours']);

/** Stored minutes → the number + unit the form shows: whole hours read as hours ("2 hours"), the rest as minutes. */
export function delayToUnit(minutes) {
  const m = Number.isFinite(minutes) ? minutes : POST_OP_DEFAULTS.delayMinutes;
  if (m >= 60 && m % 60 === 0) return { value: m / 60, unit: 'hours' };
  return { value: m, unit: 'minutes' };
}

/** The typed number + unit → minutes. NaN when the number is blank or not a number (the validator then flags it). */
export function unitToMinutes(value, unit) {
  const text = typeof value === 'string' ? value.trim() : value;
  if (text === '' || text === null || text === undefined) return NaN;
  const n = Number(text);
  if (!Number.isFinite(n)) return NaN;
  const minutes = unit === 'hours' ? n * 60 : n;
  return Number.isInteger(minutes) ? minutes : NaN;
}

function clock12(hour, minute) {
  const h = ((hour + 11) % 12) + 1;
  return `${h}:${String(minute).padStart(2, '0')} ${hour < 12 ? 'AM' : 'PM'}`;
}

/** 'HH:mm' → '8:00 AM'. */
export function formatClock(hhmm) {
  const m = /^(\d{2}):(\d{2})$/.exec(String(hhmm));
  return m ? clock12(Number(m[1]), Number(m[2])) : String(hhmm);
}

/** The quiet-hours dropdown: every hour from 6 AM to 10 PM (design). */
export const TIME_OPTIONS = Object.freeze((() => {
  const list = [];
  for (let h = 6; h <= 22; h++) list.push(Object.freeze({ value: `${String(h).padStart(2, '0')}:00`, text: clock12(h, 0) }));
  return list;
})());

/** TIME_OPTIONS plus any saved value that is not on the list (e.g. 08:30), so a select never silently changes a stored time. */
export function timeOptionsWith(...values) {
  const extras = values.filter((v) => HHMM_RE.test(String(v)));
  const out = TIME_OPTIONS.slice();
  extras.forEach((v) => {
    if (!out.some((o) => o.value === v)) out.push({ value: v, text: formatClock(v) });
  });
  return out.sort((a, b) => (a.value < b.value ? -1 : 1));
}

/** The confirm dialog / switch line: "Messages go 60 minutes after completion, between 8:00 AM and 8:00 PM." */
export function whenSentText(draft) {
  const d = draft || {};
  const { value, unit } = delayToUnit(d.delayMinutes);
  const unitText = value === 1 ? unit.replace(/s$/, '') : unit;
  return `Messages go ${value} ${unitText} after completion, between ${formatClock(d.quietStart)} and ${formatClock(d.quietEnd)}.`;
}

/**
 * The switch card's timestamp: "Oct 15, 2026, 2:10 PM" in the practice's timezone. `liveSince` is stamped by the SERVER
 * (an ISO UTC string) — this only FORMATS it, it never computes it. An unknown timezone falls back to the browser's.
 */
export function formatLiveSince(iso, timeZone) {
  if (!nonBlank(iso) || !Number.isFinite(Date.parse(iso))) return '';
  const options = { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' };
  try {
    return new Intl.DateTimeFormat('en-US', Object.assign({ timeZone: timeZone || undefined }, options)).format(new Date(iso));
  } catch (e) {
    return new Intl.DateTimeFormat('en-US', options).format(new Date(iso));
  }
}

// ── Live preview ─────────────────────────────────────────────────────────────────────────────────────────────

/** The fictional patient (no PHI in a settings screen). The PRACTICE is the site's real one. */
export const PREVIEW_PATIENT = Object.freeze({ firstName: 'Maria', lastName: 'Lopez' });

/** Two sample post-op forms: "1 procedure" shows the first, "2 procedures" both. */
export const PREVIEW_FORMS = Object.freeze([
  Object.freeze({ label: 'Extraction', title: 'After Your Extraction', url: 'https://prod.teamio.app/l/8KX2mQ4' }),
  Object.freeze({ label: 'Bone graft', title: 'After Your Bone Graft', url: 'https://prod.teamio.app/l/3RQ7aB9' }),
]);

export function previewProcedureText(count) {
  return count === 2 ? 'Extraction and bone graft' : 'Extraction';
}

const TOKEN_RE = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;

// `extra` (optional, Forms Settings): { vars } adds / overrides sample values ({{apt_date}}, {{form_link}}); { formLinkTitle } makes an email
// paragraph that is ONLY {{form_link}} a link card with that title. Absent = exactly what the post-op preview always drew.
function previewVars(site, count, extra) {
  const s = site || {};
  const vars = {
    patient_first_name: PREVIEW_PATIENT.firstName,
    patient_last_name: PREVIEW_PATIENT.lastName,
    patient_name: `${PREVIEW_PATIENT.firstName} ${PREVIEW_PATIENT.lastName}`,
    site_name: nonBlank(s.name) ? s.name : 'Your practice',
    site_officephone: nonBlank(s.phone) ? s.phone : '(office phone)',
    forms: PREVIEW_FORMS.slice(0, count === 2 ? 2 : 1),
  };
  if (extra && extra.vars && typeof extra.vars === 'object') {
    Object.keys(extra.vars).forEach((key) => { if (key !== 'forms') vars[key] = extra.vars[key]; });
  }
  return vars;
}

/** The same rule the sender uses: ONE form → the URL alone; several → one "Label: url" line each. */
export function previewLinksText(forms) {
  if (forms.length === 1) return forms[0].url;
  return forms.map((f) => `${f.label}: ${f.url}`).join('\n');
}

/** A template with the sample values filled in (an unknown token renders empty, like the sender). Plain text. */
export function fillPreviewText(template, site, count, extra) {
  const vars = previewVars(site, count, extra);
  return String(template === null || template === undefined ? '' : template).replace(TOKEN_RE, (_m, key) => {
    if (key === 'post_op_links') return previewLinksText(vars.forms);
    if (key === 'email_signature_block') return '';
    return Object.prototype.hasOwnProperty.call(vars, key) && key !== 'forms' ? String(vars[key]) : '';
  }).trim();
}

/** The text bubble, split so the links can be underlined: [{ text, link }]. */
export function previewSmsParts(template, site, count, extra) {
  const text = fillPreviewText(template, site, count, extra);
  return text.split(/(https?:\/\/[^\s]+)/).filter((t) => t !== '').map((t) => ({ text: t, link: /^https?:\/\//.test(t) }));
}

/** "142 characters · 1 text" from analyzeSms().length / .segments (the reuse happens in MessageTab.vue). */
export function smsCounterText(length, segments) {
  const n = Number.isFinite(segments) && segments > 0 ? segments : 1;
  return `${length} ${length === 1 ? 'character' : 'characters'} · ${n} ${n === 1 ? 'text' : 'texts'}`;
}

/**
 * The email card: { subject, blocks } where a block is
 *   { type:'p', text }             a paragraph (single newlines kept)
 *   { type:'links' }               a paragraph that is ONLY {{post_op_links}} → the link cards
 *   { type:'signature' }           a paragraph that is ONLY {{email_signature_block}} and the office's signature is ON
 *   { type:'signatureOff' }        …and it is off → the dashed "turn it on in Settings → Email" note
 *   { type:'formLink', title }     Forms Settings only (`extra.formLinkTitle` given): a paragraph that is ONLY {{form_link}} → one link card
 * `extra` = { vars, formLinkTitle } (see previewVars). Mirrors the sender's lib/postOp/render.js: blank lines split paragraphs; an inline signature token renders empty.
 * `site.signatureEnabled` decides signature vs signatureOff; the signature HTML itself is rendered by the component.
 */
export function previewEmail(email, site, count, extra) {
  const e = email && typeof email === 'object' ? email : {};
  const forms = previewVars(site, count, extra).forms;
  const blocks = [];
  String(e.body === null || e.body === undefined ? '' : e.body).replace(/\r\n?/g, '\n').split(/\n{2,}/).forEach((raw) => {
    const paragraph = raw.trim();
    if (!paragraph) return;
    const only = /^\{\{\s*([A-Za-z0-9_]+)\s*\}\}$/.exec(paragraph);
    if (only && only[1] === 'post_op_links') { blocks.push({ type: 'links', forms }); return; }
    if (only && only[1] === 'form_link' && extra && typeof extra.formLinkTitle === 'string') { // Forms Settings: the one Form link, drawn as a card
      blocks.push({ type: 'formLink', title: extra.formLinkTitle });
      return;
    }
    if (only && only[1] === 'email_signature_block') {
      blocks.push({ type: site && site.signatureEnabled ? 'signature' : 'signatureOff' });
      return;
    }
    const text = fillPreviewText(paragraph.replace(/\{\{\s*post_op_links\s*\}\}/g, forms.map((f) => f.label).join(', ')), site, count, extra);
    if (text) blocks.push({ type: 'p', text });
  });
  return { subject: fillPreviewText(e.subject, site, count, extra).replace(/\s*[\r\n]+\s*/g, ' '), blocks };
}
